import { ChangeEvent, useRef } from 'react';
import { z } from 'zod';

import { Form, InputField, SelectField } from '@/components/Form';
import { useUpdateUser } from '@/features/users';
import { AuthUser } from '@/features/auth';

import { ContributorDTO } from '../../users/types';
import { useChangeContributorRole } from '../api/changeContributorRole';
import { useRoles } from '../api/getRoles';
import { Contributor } from '../types';

import { ContributorMenu } from './ContributorMenu';

const schema = z.object({
  name: z.string().min(1, 'Required'),
  email: z.string().min(1, 'Required'),
  role_id: z.number(),
});

interface ContributorProps {
  contributor: Contributor;
}

const statusOpts = [
  {
    label: 'ACTIVE',
    value: 'ACTIVE',
  },
  {
    label: 'CANCELED',
    value: 'CANCELED',
  },
];

export const ContributorItem = ({ contributor }: ContributorProps) => {
  const isDirtyRef = useRef(false);
  const { company, user, role } = contributor;
  const changeContributorRoleMutation = useChangeContributorRole({});
  const updateUserMutation = useUpdateUser({ userId: user.id });

  const onRoleChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    const roleId = +event.target.value;

    await changeContributorRoleMutation.mutateAsync({
      companyId: `${company.id}`,
      userId: user.id,
      prevRoleId: role.id,
      nextRoleId: roleId,
    });

    isDirtyRef.current = false;
  };

  /**
   * update user status
   *
   * @date 2025/09/30
   * @param event
   */
  const onStatusChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    const status = event.target.value;
    await updateUserMutation.mutateAsync({ data: { status } });

    isDirtyRef.current = false;
  };

  const handleUserUpdate = async (data: Partial<AuthUser>) => {
    if (isDirtyRef.current) {
      await updateUserMutation.mutateAsync({ data });
      isDirtyRef.current = false;
    }
  };

  const { roleOptions } = useRoles({});

  return (
    <div
      data-status={contributor.user.status}
      className="d-flex flex-row justify-content-between align-items-center"
    >
      <Form<ContributorDTO['data'], typeof schema>
        id={`contributor-${contributor}`}
        onSubmit={() => console.log(`>> TODO: submit...`)}
        options={{
          defaultValues: {
            name: contributor.user.name,
            email: contributor.user.email,
            status: contributor.user.status,
            role_id: contributor.role.id,
          },
        }}
        schema={schema}
        className="d-flex justify-content-start w-100 gap-3"
      >
        {({ register, formState, getValues }) => {
          const { isDirty } = formState;

          const { name, email } = getValues();

          isDirtyRef.current = isDirty;

          return (
            <>
              <InputField
                label="Username"
                error={formState.errors['name']}
                registration={register('name', {
                  disabled: true,
                  onBlur: () => handleUserUpdate({ name }),
                })}
                placeholder="Username"
                labelStyles="fs-6"
                className="mx-1"
                wrapperStyles="w-auto"
              />

              <InputField
                label="Email"
                type="email"
                error={formState.errors['email']}
                registration={register('email', {
                  disabled: true,
                  onBlur: () => handleUserUpdate({ email }),
                })}
                placeholder="example@mail.com"
                labelStyles="fs-6 w-100"
                wrapperStyles="w-30"
              />
              {roleOptions && (
                <SelectField
                  name="role_id"
                  label="Access level"
                  className="px-2"
                  labelStyles="fs-6 "
                  wrapperStyles="mx-1"
                  placeholder="Select Role"
                  error={formState.errors['status']}
                  registration={register('role_id', {
                    onChange: onRoleChange,
                    value: contributor.role.id,
                  })}
                  options={roleOptions}
                />
              )}
              {/* statu setting: ACTIVE | CANCELED - @2025/09/30 */}
              <SelectField
                name="status"
                label="Status"
                className="px-2"
                labelStyles="fs-6 w-100"
                wrapperStyles="w-128"
                placeholder="Select Status"
                error={formState.errors['status']}
                registration={register('status', {
                  onChange: onStatusChange,
                  value: contributor.user.status,
                })}
                options={statusOpts}
              />
              {/* <div className="p-2"></div> */}
            </>
          );
        }}
      </Form>
      <ContributorMenu contributor={contributor} />
    </div>
  );
};
